import React, { useEffect, useState, useMemo } from 'react';
import { message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import {
  fetchAssignmentDevices,
  deleteAssignmentDevice,
  setAssignmentFilteredDevices,
  DeviceAssignmentState,
} from '../../redux/DeviceAssignment';
import { Device, StoreGroup } from './types';
import {
  MESSAGES,
  STORE_UNASSIGNED_ID,
  STORE_UNASSIGNED_NAME,
  DEFAULT_MERCHANT_NAME,
} from './constants';
import Header from './Header';
import SearchBar from './SearchBar';
import StoreGroupCard from './StoreGroupCard';
import EmptyState from './EmptyState';
import DeviceModal from './DeviceModal';
import { Container, StoreGroupsContainer } from './styles';

const DeviceAssignment: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { devices, filteredDevices } = useSelector(
    (state: RootState): DeviceAssignmentState => state.deviceAssignment
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState<Device | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    dispatch(fetchAssignmentDevices());
  }, [dispatch]);

  useEffect(() => {
    let filtered = [...devices];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((device) => {
        const deviceName = device.name?.toLowerCase() || '';
        const deviceIdentifier = device.identifier?.toLowerCase() || '';
        const typeName =
          typeof device.type === 'object'
            ? device.type?.name?.toLowerCase()
            : '';
        const storeName =
          typeof device.store === 'object'
            ? device.store?.name?.toLowerCase()
            : '';
        const merchantName =
          typeof device.merchant === 'object'
            ? device.merchant?.name?.toLowerCase()
            : '';

        return (
          deviceName.includes(query) ||
          deviceIdentifier.includes(query) ||
          typeName.includes(query) ||
          storeName.includes(query) ||
          merchantName.includes(query)
        );
      });
    }

    dispatch(setAssignmentFilteredDevices(filtered));
  }, [devices, searchQuery, dispatch]);

  const storeGroups = useMemo((): Record<string, StoreGroup> => {
    if (!filteredDevices || filteredDevices.length === 0) return {};

    return filteredDevices.reduce((acc, device) => {
      const storeId =
        typeof device.store === 'object'
          ? device.store?.id || STORE_UNASSIGNED_ID
          : STORE_UNASSIGNED_ID;
      const storeName =
        typeof device.store === 'object'
          ? device.store?.name || STORE_UNASSIGNED_NAME
          : STORE_UNASSIGNED_NAME;
      const merchantName =
        typeof device.merchant === 'object'
          ? device.merchant?.name || DEFAULT_MERCHANT_NAME
          : DEFAULT_MERCHANT_NAME;

      if (!acc[storeId]) {
        acc[storeId] = {
          storeId,
          storeName,
          merchantName,
          devices: [],
        };
      }

      acc[storeId].devices.push(device);
      return acc;
    }, {} as Record<string, StoreGroup>);
  }, [filteredDevices]);

  const handleAddDevice = () => {
    setCurrentDevice(null);
    setIsModalOpen(true);
  };

  const handleEditDevice = (device: Device) => {
    setCurrentDevice(device);
    setIsModalOpen(true);
  };

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await dispatch(deleteAssignmentDevice(deviceId)).unwrap();
      message.success(MESSAGES.DEVICE_DELETED);
    } catch (error: any) {
      message.error(error || MESSAGES.ERROR_DELETE);
    }
  };

  const handleSave = () => {
    setIsModalOpen(false);
    setCurrentDevice(null);
    dispatch(fetchAssignmentDevices());
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentDevice(null);
  };

  const hasFilters = !!searchQuery;
  const groupEntries = Object.values(storeGroups);

  return (
    <Container>
      <Header onAddDevice={handleAddDevice} />

      <SearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {groupEntries.length === 0 ? (
        <EmptyState hasFilters={hasFilters} isDark={isDarkMode} />
      ) : (
        <StoreGroupsContainer>
          {groupEntries.map((group) => (
            <StoreGroupCard
              key={group.storeId}
              storeGroup={group}
              isDark={isDarkMode}
              onEditDevice={handleEditDevice}
              onDeleteDevice={handleDeleteDevice}
            />
          ))}
        </StoreGroupsContainer>
      )}

      <DeviceModal
        open={isModalOpen}
        initialDevice={currentDevice}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </Container>
  );
};

export default DeviceAssignment;
